import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRightLeft, Building2, FileX2, FilePlus2, CheckCircle2, Loader2, AlertTriangle, UserX, Paperclip } from "lucide-react";

const TAB_META = [
  { key: "move",      label: "Mover a otro mandante",     icon: ArrowRightLeft, color: "text-blue-600 border-blue-600" },
  { key: "unassign",  label: "Sin asignación",             icon: UserX,          color: "text-amber-600 border-amber-600" },
  { key: "traslado",  label: "Anexo de traslado",          icon: Building2,      color: "text-amber-600 border-amber-600" },
  { key: "finiquito", label: "Finiquito",                  icon: FileX2,         color: "text-red-600 border-red-600" },
];

export default function ReassignDialog({ open, onClose, worker, onDone, defaultTab = "move" }) {
  const [tab, setTab] = useState(defaultTab);
  const [mandantes, setMandantes] = useState([]);
  const [causales, setCausales] = useState([]);
  const [mandante, setMandante] = useState("");
  const [contrato, setContrato] = useState("");
  const [causal, setCausal] = useState("");
  const [note, setNote] = useState("");
  const [fecha, setFecha] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/mandantes").then((r) => setMandantes(r.data)).catch(() => {});
    api.get("/finiquito/causales").then((r) => setCausales(r.data)).catch(() => {});
    setTab(defaultTab || "move");
    setMandante(""); setContrato(""); setCausal(""); setNote(""); setPreview(null);
    setFecha(new Date().toISOString().slice(0, 10)); setFile(null);
  }, [open, defaultTab]);

  const contratos = useMemo(() => (mandantes.find((m) => m.name === mandante)?.contratos) || [], [mandantes, mandante]);

  const loadPreview = async (mName, cName) => {
    if (!worker?.id || !mName) { setPreview(null); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/workers/${worker.id}/reassign/preview`, { action: "move", mandante: mName, contrato: cName || "" });
      setPreview(data);
    } catch { setPreview(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (tab === "move" && mandante) loadPreview(mandante, contrato); // eslint-disable-next-line
  }, [tab, mandante, contrato]);

  const submit = async () => {
    if (!worker?.id) return;
    if (tab === "move" && !mandante) { toast.error("Elige el mandante destino"); return; }
    if (tab === "move" && isSameAssignment) { toast.error("El trabajador ya está en ese mandante y contrato"); return; }
    if (tab === "finiquito" && !causal) { toast.error("Elige una causal"); return; }
    if ((tab === "finiquito" || tab === "traslado") && !fecha) { toast.error("Elige la fecha"); return; }
    if (file && !file.name.toLowerCase().endsWith(".pdf")) { toast.error("El archivo debe ser PDF"); return; }
    setSubmitting(true);
    try {
      const body = { action: tab, note };
      if (tab === "move") { body.mandante = mandante; body.contrato = contrato || ""; }
      if (tab === "finiquito") { body.causal = causal; body.fecha = fecha; }
      if (tab === "traslado") { body.fecha = fecha; }
      await api.post(`/workers/${worker.id}/reassign`, body);
      if (file && (tab === "finiquito" || tab === "traslado")) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", tab);
        await api.post(`/workers/${worker.id}/finiquito/file`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(
        tab === "move" ? "Trabajador reasignado" :
        tab === "finiquito" ? "Finiquito registrado" :
        tab === "unassign" ? "Trabajador sin asignación" :
        "Anexo de traslado registrado"
      );
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo procesar");
    } finally { setSubmitting(false); }
  };

  const currentMandante = worker?.mandante || worker?.company || "—";
  const currentContrato = worker?.contrato || "—";
  const isSameAssignment = tab === "move"
    && mandante
    && mandante === (worker?.mandante || worker?.company || "")
    && (contrato || "") === (worker?.contrato || "");

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b border-slate-100">
          <DialogTitle className="font-display text-xl">Reasignar trabajador</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            <span className="font-medium text-slate-700">{worker?.first_name} {worker?.last_name}</span> · Actualmente en {currentMandante}{worker?.contrato ? ` · ${currentContrato}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex border-b border-slate-100 bg-slate-50/60">
          {TAB_META.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} data-testid={`reassign-tab-${t.key}`} onClick={() => { setTab(t.key); setPreview(null); }}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${active ? `${t.color} bg-white` : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
          {tab === "move" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Mandante destino</label>
                  <Select value={mandante} onValueChange={(v) => { setMandante(v); setContrato(""); }}>
                    <SelectTrigger data-testid="reassign-mandante" className="mt-1 bg-white"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {mandantes.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Contrato destino</label>
                  <Select value={contrato} onValueChange={setContrato} disabled={!mandante}>
                    <SelectTrigger data-testid="reassign-contrato" className="mt-1 bg-white"><SelectValue placeholder={mandante ? "Selecciona…" : "Elige primero mandante"} /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {contratos.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isSameAssignment && (
                <div data-testid="reassign-same-warning" className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                  <b>Mismo contrato:</b> el trabajador ya está asignado a <b>{mandante}</b>{contrato ? <> · <b>{contrato}</b></> : null}. Cambia el mandante o el contrato para reasignar.
                </div>
              )}

              {loading && <p className="text-sm text-slate-400 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Calculando homologación…</p>}

              {preview && !loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {preview.same_mandante && (
                    <div className="md:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                      <b>Mismo mandante:</b> el estándar de documentos es idéntico, por lo que el avance de todos los requisitos se preserva.
                    </div>
                  )}
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
                    <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1.5 mb-2">
                      <CheckCircle2 size={14} /> Avance preservado ({(preview.preserved || []).length})
                    </p>
                    {(preview.preserved || []).length === 0
                      ? <p className="text-xs text-slate-500">Ninguno de los códigos de requisito coincide con el nuevo mandante.</p>
                      : <ul className="text-xs text-slate-700 space-y-1 max-h-40 overflow-y-auto">{preview.preserved.map((d) => <li key={d.code}>· {d.name}</li>)}</ul>}
                  </div>
                  <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4">
                    <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5 mb-2">
                      <AlertTriangle size={14} /> Documentos a archivar ({preview.archive.length})
                    </p>
                    {preview.archive.length === 0
                      ? <p className="text-xs text-slate-500">Todos los documentos cargados son compatibles.</p>
                      : <ul className="text-xs text-slate-700 space-y-1 max-h-40 overflow-y-auto">{preview.archive.map((d) => <li key={d.code}>· {d.code}</li>)}</ul>}
                  </div>
                  <div className="md:col-span-2 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
                    <p className="text-xs font-semibold text-blue-700 flex items-center gap-1.5 mb-2">
                      <FilePlus2 size={14} /> Requisitos en nuevo mandante ({preview.new_required.length})
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {preview.new_required.map((r) => (
                        <span key={r.code} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">{r.name}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "finiquito" && (
            <>
              <div>
                <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Causal (Código del Trabajo de Chile)</label>
                <Select value={causal} onValueChange={setCausal}>
                  <SelectTrigger data-testid="reassign-causal" className="mt-1 bg-white"><SelectValue placeholder="Selecciona causal…" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    {causales.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <FileAndDateFields fecha={fecha} setFecha={setFecha} file={file} setFile={setFile} />
              <p className="text-xs text-slate-500">
                Al finiquitar, el trabajador queda sin asignación activa de mandante/contrato pero su expediente y documentos se conservan para auditoría.
              </p>
            </>
          )}

          {tab === "traslado" && (
            <>
              <div className="rounded-xl bg-amber-50/60 border border-amber-100 p-4 text-sm text-amber-800">
                <p className="font-semibold mb-1">Anexo de Traslado</p>
                <p className="text-xs">El trabajador queda temporalmente sin asignación. Sus documentos se mantienen y podrás reasignarlo luego a otro mandante/contrato sin recrearlo.</p>
              </div>
              <FileAndDateFields fecha={fecha} setFecha={setFecha} file={file} setFile={setFile} />
            </>
          )}

          {tab === "unassign" && (
            <div className="rounded-xl bg-amber-50/60 border border-amber-100 p-4 text-sm text-amber-800">
              <p className="font-semibold mb-1">Dejar sin asignación</p>
              <p className="text-xs">El trabajador queda activo pero sin mandante ni contrato asignado. Sus documentos se conservan. Puedes reasignarlo cuando lo necesites desde este mismo diálogo.</p>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Nota (opcional)</label>
            <textarea data-testid="reassign-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="Motivo o comentario interno…"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 focus:border-blue-400 outline-none text-sm" />
          </div>
        </div>

        <DialogFooter className="p-5 border-t border-slate-100 bg-slate-50/60 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-white border border-slate-200">
            Cancelar
          </button>
          <button data-testid="reassign-confirm" disabled={submitting || isSameAssignment} onClick={submit}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white inline-flex items-center gap-2 ${
              tab === "finiquito" ? "bg-red-600 hover:bg-red-700" :
              tab === "traslado" || tab === "unassign" ? "bg-amber-600 hover:bg-amber-700" :
              "bg-blue-600 hover:bg-blue-700"
            } disabled:opacity-60`}>
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {tab === "move" ? "Reasignar" :
             tab === "finiquito" ? "Finiquitar" :
             tab === "unassign" ? "Dejar sin asignación" :
             "Registrar traslado"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileAndDateFields({ fecha, setFecha, file, setFile }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">Fecha efectiva</label>
        <input data-testid="reassign-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
          className="mt-1 w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
      </div>
      <div>
        <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">PDF del documento (opcional)</label>
        <label className="mt-1 flex items-center gap-2 h-9 px-3 rounded-lg border border-dashed border-slate-300 text-sm text-slate-600 hover:bg-slate-50 cursor-pointer">
          <Paperclip size={13} className="text-slate-400" />
          <span className="truncate">{file ? file.name : "Adjuntar PDF…"}</span>
          <input data-testid="reassign-file" type="file" accept="application/pdf" className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
      </div>
    </div>
  );
}
